<template>
  <div class="page-layout">
    <div class="page-content">
      <h1>Новое событие</h1>
      <form class="panel event-form" @submit.prevent="submit">
        <label>
          Название
          <input v-model="name" type="text" class="input" required />
        </label>

        <label>
          Обложка
          <input type="file" accept="image/jpeg,image/png,image/webp" @change="onCoverSelected" />
        </label>
        <div v-if="coverPreviewUrl" class="event-cover-preview">
          <img :src="coverPreviewUrl" alt="Превью обложки" />
          <button type="button" class="btn btn-secondary btn-sm" @click="clearCover">Удалить</button>
        </div>

        <label>
          Описание
          <RichTextEditor
            v-model="description"
            :allow-images="false"
            placeholder="Расскажите о событии..."
          />
        </label>

        <label>
          Начало
          <input v-model="startsAt" type="datetime-local" class="input" required />
        </label>

        <label>
          Окончание
          <input v-model="endsAt" type="datetime-local" class="input" required />
        </label>

        <div class="form-field">
          <label for="event-venue">Место проведения</label>
          <CityPicker
            ref="venuePickerRef"
            v-model="venue"
            mode="place"
            input-id="event-venue"
            placeholder="Начните вводить адрес или название"
          />
          <p class="field-hint">
            Начните вводить и выберите место из списка. Показывается на странице события, если треки не
            привязаны.
          </p>
        </div>

        <TrackMultiPicker v-model="trackIds" />

        <OrganizerPicker v-model="organizers" />

        <label class="checkbox-label">
          <input v-model="isPublic" type="checkbox" />
          Публичное событие
        </label>

        <label class="checkbox-label">
          <input v-model="allowComments" type="checkbox" />
          Разрешить комментарии
        </label>

        <p v-if="error" class="error-message">{{ error }}</p>

        <div class="btn-row">
          <button class="btn" type="submit" :disabled="submitting">
            {{ submitting ? 'Сохранение...' : 'Сохранить' }}
          </button>
          <router-link class="btn btn-secondary" :to="{ name: 'events-home' }">Отмена</router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import RichTextEditor from '../components/RichTextEditor.vue'
import CityPicker from '../components/CityPicker.vue'
import TrackMultiPicker from '../components/TrackMultiPicker.vue'
import OrganizerPicker from '../components/OrganizerPicker.vue'
import { createEvent } from '../api/events'
import { organizersToApi } from '../utils/organizerDisplay'

const router = useRouter()

const name = ref('')
const description = ref('')
const startsAt = ref('')
const endsAt = ref('')
const venue = ref(null)
const venuePickerRef = ref(null)
const trackIds = ref([])
const organizers = ref([])
const isPublic = ref(false)
const allowComments = ref(false)
const coverPhoto = ref(null)
const coverPreviewUrl = ref('')
const submitting = ref(false)
const error = ref('')

function onCoverSelected(event) {
  const file = event.target.files?.[0]
  if (!file) return

  if (coverPreviewUrl.value) URL.revokeObjectURL(coverPreviewUrl.value)
  coverPhoto.value = file
  coverPreviewUrl.value = URL.createObjectURL(file)
}

function clearCover() {
  if (coverPreviewUrl.value) URL.revokeObjectURL(coverPreviewUrl.value)
  coverPhoto.value = null
  coverPreviewUrl.value = ''
}

function venuePayload() {
  const selection = venuePickerRef.value?.getSelection?.()
  if (selection?.city) {
    return {
      venue: selection.city,
      venue_lat: selection.lat,
      venue_lng: selection.lng,
    }
  }

  return { venue: '', venue_lat: null, venue_lng: null }
}

async function submit() {
  if (venuePickerRef.value?.hasUnconfirmedInput?.()) {
    error.value = 'Выберите место проведения из списка подсказок'
    return
  }

  submitting.value = true
  error.value = ''
  try {
    const { data } = await createEvent(
      {
        name: name.value,
        description: description.value,
        starts_at: new Date(startsAt.value).toISOString(),
        ends_at: new Date(endsAt.value).toISOString(),
        ...venuePayload(),
        track_ids: trackIds.value,
        organizers: organizersToApi(organizers.value),
        public: isPublic.value,
        allow_comments: allowComments.value,
      },
      { coverPhoto: coverPhoto.value }
    )
    router.push({ name: 'event', params: { id: data.id } })
  } catch (err) {
    const errors = err.response?.data?.errors
    error.value = errors?.join(', ') || err.response?.data?.error || 'Не удалось создать событие'
  } finally {
    submitting.value = false
  }
}

onBeforeUnmount(() => {
  if (coverPreviewUrl.value) URL.revokeObjectURL(coverPreviewUrl.value)
})
</script>

<style scoped>
.event-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.event-form label {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-weight: 600;
}

.event-form .input,
.event-form input[type='text'],
.event-form input[type='datetime-local'] {
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
}

.checkbox-label {
  flex-direction: row !important;
  align-items: center;
  font-weight: 500 !important;
}

.event-cover-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}

.event-cover-preview img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 0.5rem;
  object-fit: cover;
}

.field-hint {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  color: #64748b;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
</style>
